// Reads and writes of the master production schedule (spec 03 §5 rows 18 to
// 24), all through the generated client. Every write carries a fresh
// Idempotency-Key.

import { useCallback } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead, type ReadContext } from "../../../../lib/query";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];
export type MpsPlanSummary = S["MpsPlanSummary"];
export type MpsPlan = S["MpsPlan"];
export type MpsPlanDetail = S["MpsPlanDetail"];
export type MpsPlanProduct = S["MpsPlanProduct"];
export type MpsLine = S["MpsLine"];
export type MpsReceiptDetail = S["MpsReceiptDetail"];
export type MpsPlanStatus = MpsPlan["status"];

/** The statuses of the facility's one open plan (spec 03 §2.7). */
export const MPS_OPEN_STATUSES: readonly MpsPlanStatus[] = [
  "proposed",
  "adjusting",
  "pending_approval",
];

/** The statuses whose cells a planner may still change. */
export const MPS_EDITABLE_STATUSES: readonly MpsPlanStatus[] = ["proposed", "adjusting"];

/** How often the board and the tile re-read which plan is current, so a proposal shows unasked. */
export const MPS_POLL_MS = 60_000;

/** The longest override reason, approve note and reject note (contracts `MPS_NOTE_MAX`). */
export const MPS_NOTE_MAX = 500;

export const mpsKeys = {
  all: (facilityId: string) => ["planning", "mps", facilityId] as const,
  current: (facilityId: string) => ["planning", "mps", facilityId, "current"] as const,
  plans: (facilityId: string, cursor: string | undefined) =>
    ["planning", "mps", facilityId, "plans", cursor ?? null] as const,
  plan: (planId: string) => ["planning", "mps-plan", planId] as const,
  members: () => ["planning", "ref", "members"] as const,
};

export function isOpenStatus(status: MpsPlanStatus): boolean {
  return MPS_OPEN_STATUSES.includes(status);
}

export function isEditableStatus(status: MpsPlanStatus): boolean {
  return MPS_EDITABLE_STATUSES.includes(status);
}

async function listPlans(
  facilityId: string,
  query: { status?: MpsPlanStatus; cursor?: string; limit: number },
  signal: AbortSignal,
) {
  return unwrap(
    await client.GET("/v1/mps/plans", {
      signal,
      params: { query: { facility_id: facilityId, ...query } },
    }),
    "MPS plans",
  );
}

/**
 * The plan the board opens: the facility's open plan when one exists, else its
 * approved plan, else null (spec 03 §4.4). The list is newest first and the
 * open plan, when there is one, is always the newest.
 */
export async function readCurrentPlan(
  facilityId: string,
  signal: AbortSignal,
): Promise<MpsPlanSummary | null> {
  const page = await listPlans(facilityId, { limit: 20 }, signal);
  const open = page.items.find((p) => isOpenStatus(p.status));
  if (open !== undefined) return open;
  const approved = page.items.find((p) => p.status === "approved");
  if (approved !== undefined) return approved;
  if (page.next_cursor === null) return null;
  const only = await listPlans(facilityId, { status: "approved", limit: 1 }, signal);
  return only.items[0] ?? null;
}

export function useCurrentMpsPlan(facilityId: string) {
  return useQuery({
    queryKey: mpsKeys.current(facilityId),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    refetchInterval: MPS_POLL_MS,
    queryFn: async ({ signal }: ReadContext) => readCurrentPlan(facilityId, signal),
  });
}

/** One page of the facility's plans, newest first, for the Plans list. */
export function useMpsPlans(facilityId: string, cursor: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: mpsKeys.plans(facilityId, cursor),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext) =>
      listPlans(facilityId, { limit: 20, ...(cursor !== undefined ? { cursor } : {}) }, signal),
  });
}

/**
 * A plan's header and its cells, 50 products a page. The header of the first
 * page is the plan as last read; every page is re-read after a write so the
 * recomputed projection reaches every bucket.
 */
export function useMpsPlanPages(planId: string | undefined) {
  return useInfiniteQuery({
    queryKey: mpsKeys.plan(planId ?? ""),
    enabled: planId !== undefined && planId.length > 0,
    retry: shouldRetryRead,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: MpsPlanDetail) => last.next_cursor ?? undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await client.GET("/v1/mps/plans/{id}", {
          signal,
          params: {
            path: { id: planId ?? "" },
            query: pageParam !== undefined ? { cursor: pageParam } : {},
          },
        }),
        "MPS plan",
      ),
  });
}

/** The products, lines and newest header of every loaded page. */
export function flattenPages(pages: readonly MpsPlanDetail[] | undefined): {
  plan: MpsPlan | undefined;
  products: MpsPlanProduct[];
  lines: MpsLine[];
} {
  if (pages === undefined || pages.length === 0) {
    return { plan: undefined, products: [], lines: [] };
  }
  const plan = pages[0]?.plan;
  return {
    plan,
    products: pages.flatMap((p) => p.products),
    lines: pages.flatMap((p) => p.lines),
  };
}

/**
 * User id to display name, read only by a reader who may list members
 * (`identity.view_members`, the group and facility admins). Others see the
 * short id, which reads as an identifier rather than a broken name.
 */
export function useMemberNames(enabled: boolean) {
  const query = useQuery({
    queryKey: mpsKeys.members(),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async ({ signal }: ReadContext) =>
      unwrap(
        await client.GET("/v1/members", { signal, params: { query: { limit: 200 } } }),
        "members",
      ),
  });
  const byId = new Map<string, string>();
  for (const member of query.data?.items ?? []) byId.set(member.user_id, member.display_name);
  return { byId, isPending: enabled && query.isPending };
}

// ---- writes ------------------------------------------------------------------

/** `POST /v1/mps/plans/propose`: a new plan from the latest refresh. */
export async function proposeMps(facilityId: string): Promise<MpsPlan> {
  return unwrap(
    await client.POST("/v1/mps/plans/propose", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { facility_id: facilityId },
    }),
    "MPS proposal",
  );
}

export async function overrideMpsLine(
  planId: string,
  lineId: string,
  body: {
    override_qty: string | null;
    override_reason: string | null;
    expected_updated_at: string;
  },
) {
  return unwrap(
    await client.POST("/v1/mps/plans/{id}/lines/{lineId}/override", {
      params: {
        path: { id: planId, lineId },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
      body,
    }),
    "MPS override",
  );
}

export async function submitMps(planId: string): Promise<MpsPlan> {
  return unwrap(
    await client.POST("/v1/mps/plans/{id}/submit", {
      params: { path: { id: planId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: {},
    }),
    "MPS submit",
  );
}

export async function approveMps(planId: string, note: string | undefined): Promise<MpsPlan> {
  return unwrap(
    await client.POST("/v1/mps/plans/{id}/approve", {
      params: { path: { id: planId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: note !== undefined && note.length > 0 ? { note } : {},
    }),
    "MPS approve",
  );
}

export async function rejectMps(planId: string, note: string): Promise<MpsPlan> {
  return unwrap(
    await client.POST("/v1/mps/plans/{id}/reject", {
      params: { path: { id: planId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { note },
    }),
    "MPS reject",
  );
}

/** Re-reads every MPS read of a facility and the plans named, after a write. */
export function useMpsRefresh() {
  const qc = useQueryClient();
  return useCallback(
    (facilityId: string, planIds: readonly string[] = []) =>
      Promise.all([
        refreshQuery(qc, mpsKeys.all(facilityId)),
        ...planIds.map((id) => refreshQuery(qc, mpsKeys.plan(id))),
      ]).then(() => undefined),
    [qc],
  );
}
